import React from 'react';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Currency, formatCLP, formatUF } from '../../../common/format';

export interface ChartSeriesConfig {
  dataKey: string;
  name: string;
  stroke: string;
  strokeDasharray?: string;
}

export interface ChartMarker {
  xValue: number;
  yDataKey: string;
  label: string;
  color: string;
}

interface EvolutionChartProps {
  title?: string;
  data: Record<string, number>[];
  series: ChartSeriesConfig[];
  currency: Currency;
  xKey?: string;
  xLabel?: string;
  height?: number;
  markers?: ChartMarker[];
}

function formatAxisValue(value: number, currency: Currency): string {
  if (currency === 'CLP') {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
    return `$${value.toFixed(0)}`;
  }
  return `${value.toFixed(0)} UF`;
}

function formatTooltipValue(value: number, currency: Currency): string {
  return currency === 'CLP' ? formatCLP(value) : formatUF(value);
}

function resolveMarkerY(
  data: Record<string, number>[],
  xKey: string,
  xValue: number,
  yDataKey: string,
): number | null {
  const row = data.find((r) => r[xKey] === xValue);
  if (!row) return null;
  const y = row[yDataKey];
  return typeof y === 'number' ? y : null;
}

export function EvolutionChart({
  title = 'Evolución de la póliza',
  data,
  series,
  currency,
  xKey = 'year',
  xLabel = 'Año',
  height = 360,
  markers,
}: EvolutionChartProps) {
  return (
    <div className="card">
      <div className="card-header">
        <strong>{title}</strong>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={data}
            margin={{
              top: 8, right: 24, bottom: 8, left: 8,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 12 }}
              label={{
                value: xLabel, position: 'insideBottom', offset: -4, fontSize: 12,
              }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(value: number) => formatAxisValue(value, currency)}
              width={80}
            />
            <Tooltip
              formatter={(value) => formatTooltipValue(value as number, currency)}
              labelFormatter={(label) => `${xLabel} ${+label}`}
            />
            <Legend />
            {series.map((s) => (
              <Line
                key={s.dataKey}
                type="linear"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.stroke}
                strokeWidth={2}
                strokeDasharray={s.strokeDasharray}
                dot={false}
              />
            ))}
            {markers?.map((m) => {
              const y = resolveMarkerY(data, xKey, m.xValue, m.yDataKey);
              if (y === null) return null;
              return (
                <ReferenceDot
                  key={`${m.xValue}-${m.yDataKey}-${m.label}`}
                  x={m.xValue}
                  y={y}
                  r={6}
                  fill={m.color}
                  stroke="#fff"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                  label={{
                    value: m.label,
                    position: 'top',
                    fontSize: 11,
                    fill: m.color,
                  }}
                />
              );
            })}
            <Brush
              dataKey={xKey}
              height={24}
              stroke="var(--bs-primary)"
              travellerWidth={8}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

type NumericRow = Record<string, number | undefined>;

export function buildEvolutionSeries<TRow extends NumericRow>(
  rows: TRow[],
  fields: Array<keyof TRow & string>,
  currency: Currency,
  ufValue: number,
  passthroughKeys: Array<keyof TRow & string> = ['year', 'age'],
): Record<string, number>[] {
  const factor = currency === 'CLP' ? ufValue : 1;
  return rows.map((row) => {
    const point: Record<string, number> = {};
    passthroughKeys.forEach((k) => {
      const v = row[k];
      if (typeof v === 'number') point[k] = v;
    });
    fields.forEach((k) => {
      const v = row[k];
      if (typeof v === 'number') point[k] = v * factor;
    });
    return point;
  });
}
